// Top toolbar above the 3-column editor grid. Carries project-wide
// state and navigation that used to live at the top of the right rail
// — moved out so the rail can be a dedicated Inspector for the
// currently-selected object (cabinet / drawer / part).
//
// One row, ~46px tall. Layout: project name + status badge + advance
// dropdown on the left, navigation buttons on the right. Buttons that
// require cabinets (BOM, cutlist, export, problems) are disabled with
// a tooltip when the project is empty.

import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Project } from "@/lib/types";
import { StatusBadge } from "@/components/StatusBadge";

interface Props {
  project: Project;
  cabinetCount: number;
  orphanCount: number;
  renderAdvance: (project: Project, onChanged: () => void) => React.ReactNode;
}

export function ProjectToolbar({
  project, cabinetCount, orphanCount, renderAdvance,
}: Props) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const empty = cabinetCount === 0;
  const navTo = (segment: string) => () => nav(`/projects/${project._id}/${segment}`);
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] }); };

  return (
    <header style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "8px 14px",
      borderBottom: "1px solid var(--line)",
      background: "rgba(255,255,255,0.02)",
      minHeight: 46,
    }}>
      {/* Left: project identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        <span className="label" style={{ margin: 0 }}>PROJECT</span>
        <span style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {project.name}
        </span>
        <StatusBadge status={project.status} size="sm" />
        <span className="muted" style={{ fontSize: 11 }}>
          {cabinetCount} cabinet{cabinetCount === 1 ? "" : "s"}
          {orphanCount > 0 && ` (+${orphanCount} hidden)`}
        </span>
        <span style={{ marginLeft: 6 }}>{renderAdvance(project, invalidate)}</span>
      </div>

      {/* Right: navigation buttons (compact, no full-width fills) */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        <button className="ghost" style={btn} onClick={navTo("room")}>Room</button>
        <button
          className="ghost"
          style={btn}
          onClick={navTo("bom")}
          disabled={empty}
          title={empty ? "Add cabinets first" : "Bill of Materials"}
        >BOM</button>
        <button
          className="ghost"
          style={btn}
          onClick={navTo("cutlist")}
          disabled={empty}
          title={empty ? "Add cabinets first" : ""}
        >Cut list</button>
        <button
          className="ghost"
          style={btn}
          onClick={navTo("export")}
          disabled={empty}
          title={empty ? "Add cabinets first" : ""}
        >Export</button>
        <button
          className="ghost"
          style={btn}
          onClick={navTo("problems")}
          disabled={empty}
          title={empty ? "Add cabinets first" : "Project-wide validation report"}
        >Problems</button>
      </div>
    </header>
  );
}

const btn: React.CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
};
